import React, { useState, useRef, useEffect } from "react";
import { MarkerType } from "@xyflow/react";
import { Sparkles, Bot, Send, X, Upload, Plus, Save, ChevronRight, FileText, User, Wand2 } from "lucide-react";
import { sanitizeHtml } from "../lib/sanitize";
import { Button } from "./ui/button";
import { saveFlow } from "../lib/flowStorage";
import { flowAiApi, manualsApi } from "../lib/api";
import { toast } from "sonner";

// AI Flow Builder \u2014 scripted state machine that builds a flow conversationally
// States: ask_topic \u2192 ask_has_list \u2192 (upload | ask_first_q) \u2192 ask_choices \u2192 ask_branch (loop) \u2192 done \u2192 save

let nodeCounter = 200;
const newNodeId = () => `n_ai_${++nodeCounter}_${Date.now()}`;

const DIAGNOSIS_HINTS = [
  "Likely component failure \u2014 inspect / replace per manual",
  "Probable airflow restriction \u2014 check filter & ducts",
  "Electrical fault suspected \u2014 follow lockout/tagout, then test",
  "Refrigerant issue \u2014 verify pressures and look for leaks",
];

export default function AIFlowBuilder({ open, onClose, onSaved }) {
  const [messages, setMessages] = useState([]);
  const [phase, setPhase] = useState("ask_topic"); // ask_topic | ask_has_list | upload | ask_first_q | ask_choices | ask_branch | naming | done
  const [topic, setTopic] = useState("");
  const [input, setInput] = useState("");
  const [draftNodes, setDraftNodes] = useState([]);
  const [draftEdges, setDraftEdges] = useState([]);
  const [pendingBranches, setPendingBranches] = useState([]); // {parentId, choice}
  const [currentBranch, setCurrentBranch] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [flowName, setFlowName] = useState("");
  const [indexedFiles, setIndexedFiles] = useState([]);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      reset();
      manualsApi
        .list()
        .then((list) => setIndexedFiles((list || []).filter((m) => m.status === "Indexed")))
        .catch(() => setIndexedFiles([]));
    }
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, isTyping]);

  const reset = () => {
    setPhase("ask_topic");
    setTopic("");
    setInput("");
    setDraftNodes([]);
    setDraftEdges([]);
    setPendingBranches([]);
    setCurrentBranch(null);
    setFlowName("");
    setMessages([
      {
        sender: "bot",
        text:
          "Hi, I'm your **AI Flow Builder**. I'll walk you through creating a diagnostic flow chart step by step. I can also reference your **trained sources** to suggest good questions.\n\nWhat mechanical issue or scenario do you want this flow to diagnose?",
      },
    ]);
  };

  const pushBot = (text, extra = {}) => {
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      setMessages((m) => [...m, { sender: "bot", text, ...extra }]);
    }, 600);
  };

  const pushUser = (text) => {
    setMessages((m) => [...m, { sender: "user", text }]);
  };

  // Helper layout: stack nodes vertically with horizontal spread for branches
  const placeNode = (parentId, indexInLevel, level) => {
    const x = 220 + indexInLevel * 220;
    const y = 60 + level * 140;
    return { x, y };
  };

  const addStartNode = () => {
    const id = newNodeId();
    setDraftNodes([
      { id, type: "startNode", position: { x: 280, y: 30 }, data: { label: "Start Diagnosis", description: topic || "Begin diagnosis" } },
    ]);
    return id;
  };

  // ---- Conversation handlers ----
  const handleTopicSubmit = () => {
    const t = input.trim();
    if (!t) return;
    setTopic(t);
    pushUser(t);
    setInput("");
    pushBot(
      `Great \u2014 we'll build a flow to diagnose **"${t}"**. How would you like to build it?\n\n\u2728 **Generate with AI** \u2014 GPT-4o creates a full diagnostic flow instantly\n\ud83d\udcc4 **Upload a list** \u2014 chain your questions linearly\n\ud83d\udd28 **Build together** \u2014 step-by-step guided builder`,
      { choices: ["Generate with AI", "Upload a list", "Build together"] }
    );
    setPhase("ask_has_list");
  };

  const handleListChoice = (choice) => {
    pushUser(choice);
    if (choice === "Generate with AI") {
      handleAiGenerate();
      return;
    }
    if (choice === "Upload a list") {
      pushBot(
        `Sure \u2014 upload a **.txt, .csv, or .pdf** with one question per line. I'll convert each into a question node.`
      );
      setPhase("upload");
      setTimeout(() => fileInputRef.current?.click(), 400);
    } else {
      const sourcesText = indexedFiles.length
        ? `Referencing **${indexedFiles.length} indexed sources** including ${indexedFiles[0].name}.`
        : "";
      pushBot(
        `Good choice. ${sourcesText}\n\nWhat is the **first question** a technician should ask when troubleshooting this issue?`,
        { hint: "e.g., \"Is the unit receiving power?\"" }
      );
      const startId = addStartNode();
      setCurrentBranch({ parentId: startId, choice: null });
      setPhase("ask_first_q");
    }
  };

  const handleAiGenerate = async () => {
    setPhase("ai_generating");
    pushBot(
      `\u2728 Generating a complete diagnostic flow for **"${topic}"** with GPT-4o\u2026 this takes ~10\u201320 seconds.`
    );
    setIsTyping(true);
    try {
      const result = await flowAiApi.generate(topic, false);
      setIsTyping(false);
      const nodes = (result.nodes || []).map((n) => ({
        id: n.id,
        type: n.type,
        position: n.position || { x: 240, y: 60 },
        data: n.data || { label: "Node" },
      }));
      const edges = (result.edges || []).map((e) => ({
        ...e,
        markerEnd: { type: MarkerType.ArrowClosed, color: "#94A3B8" },
        style: { stroke: "#94A3B8", strokeWidth: 1.6 },
      }));
      setDraftNodes(nodes);
      setDraftEdges(edges);
      setFlowName(result.name || topic);
      pushBot(
        `\u2705 Generated **"${result.name || topic}"** \u2014 ${nodes.length} nodes, ${edges.length} connections. Review the name and save to **My Flows**, or build another version.`
      );
      setPhase("naming");
    } catch (err) {
      console.error(err);
      setIsTyping(false);
      const detail = err?.response?.data?.detail || err?.message || "Unknown error";
      toast.error(`AI generation failed: ${detail}`);
      pushBot(
        `\u26a0\ufe0f AI generation failed (${detail}). Want to build it together instead?`,
        { choices: ["Build together", "Upload a list"] }
      );
      setPhase("ask_has_list");
    }
  };

  const handleQuestionListUpload = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = String(ev.target.result || "");
      const questions = text
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter((l) => l && !l.startsWith("#"))
        .slice(0, 12);
      if (questions.length === 0) {
        pushBot("I couldn't find any questions in that file. Try again, or let's build together.");
        return;
      }
      pushUser(`Uploaded ${file.name} \u2014 ${questions.length} questions found`);
      pushBot(`I parsed **${questions.length} questions** from "${file.name}" and chained them linearly with **Yes / No** branches. You can edit any node afterwards.`);

      // Build linear flow
      const startId = newNodeId();
      const nodes = [
        { id: startId, type: "startNode", position: { x: 280, y: 30 }, data: { label: "Start Diagnosis", description: topic } },
      ];
      const edges = [];
      let prevId = startId;
      questions.forEach((q, i) => {
        const id = newNodeId();
        nodes.push({
          id,
          type: "questionNode",
          position: { x: 280, y: 160 + i * 130 },
          data: { label: q, choices: ["Yes", "No"] },
        });
        edges.push(edgeBetween(prevId, id));
        prevId = id;
      });
      // End diagnosis
      const diagId = newNodeId();
      nodes.push({
        id: diagId,
        type: "diagnosisNode",
        position: { x: 280, y: 160 + questions.length * 130 },
        data: {
          label: `Likely diagnosis for ${topic}`,
          recommendation: "Final diagnosis based on the chained question answers. Edit recommendation as needed.",
        },
      });
      edges.push(edgeBetween(prevId, diagId));

      setDraftNodes(nodes);
      setDraftEdges(edges);
      setTimeout(() => askToSave(), 600);
    };
    reader.readAsText(file);
  };

  const edgeBetween = (source, target, sourceHandle, label) => ({
    id: `e_${source}_${target}_${Math.random().toString(36).slice(2, 6)}`,
    source,
    target,
    sourceHandle: sourceHandle || undefined,
    label: label || (sourceHandle || ""),
    markerEnd: { type: MarkerType.ArrowClosed, color: "#94A3B8" },
    style: { stroke: "#94A3B8", strokeWidth: 1.6 },
  });

  const handleFirstQuestion = () => {
    const q = input.trim();
    if (!q) return;
    pushUser(q);
    setInput("");
    const qId = newNodeId();
    const startNode = draftNodes.find((n) => n.type === "startNode");
    const newNode = {
      id: qId,
      type: "questionNode",
      position: { x: 240, y: 180 },
      data: { label: q, choices: ["Yes", "No"] },
    };
    setDraftNodes((nds) => [...nds, newNode]);
    setDraftEdges((eds) => [...eds, edgeBetween(startNode.id, qId)]);
    setCurrentBranch({ parentId: qId, choice: null, level: 1 });

    pushBot(
      `Got it. What are the **possible answers**? Type them comma-separated (max 4) \u2014 or click a preset.`,
      { choices: ["Yes, No", "Yes, No, Sometimes", "True, False, Unknown"] }
    );
    setPhase("ask_choices");
  };

  const handleChoicesSubmit = (text) => {
    const t = (text || input).trim();
    if (!t) return;
    pushUser(t);
    setInput("");
    const choices = t.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 4);
    const parentId = currentBranch.parentId;
    setDraftNodes((nds) => nds.map((n) => (n.id === parentId ? { ...n, data: { ...n.data, choices } } : n)));
    // Queue branches
    const branches = choices.map((c) => ({ parentId, choice: c, level: (currentBranch.level || 1) + 1 }));
    setPendingBranches(branches);
    askNextBranch(branches);
  };

  const askNextBranch = (branches) => {
    const queue = branches || pendingBranches;
    if (!queue || queue.length === 0) {
      askIfDone();
      return;
    }
    const next = queue[0];
    setCurrentBranch(next);
    setPendingBranches(queue.slice(1));
    const sourceTip =
      indexedFiles.length > 0
        ? `\n\n_Referencing ${indexedFiles[0].name} to suggest the next step._`
        : "";
    pushBot(
      `For the answer **"${next.choice}"** \u2014 what comes next?${sourceTip}`,
      {
        choices: ["Ask another question", "Final Diagnosis", "Recommend Product"],
        meta: { branchChoice: next.choice },
      }
    );
    setPhase("ask_branch");
  };

  const handleBranchType = (typeChoice) => {
    pushUser(typeChoice);
    if (typeChoice === "Ask another question") {
      pushBot(`Type the next **question** for the "${currentBranch.choice}" path:`);
      setPhase("ask_followup_q");
    } else if (typeChoice === "Final Diagnosis") {
      pushBot(`Type the **diagnosis label** for the "${currentBranch.choice}" path:`);
      setPhase("ask_diagnosis_label");
    } else if (typeChoice === "Recommend Product") {
      // Auto-create a Recommend Product placeholder node
      const id = newNodeId();
      const xCount = draftNodes.filter((n) => n.type === "recommendProductNode").length;
      const newNode = {
        id,
        type: "recommendProductNode",
        position: { x: 80 + xCount * 270, y: 460 + (currentBranch.level || 1) * 60 },
        data: { label: `Recommend for ${currentBranch.choice}`, productIds: [] },
      };
      setDraftNodes((nds) => [...nds, newNode]);
      setDraftEdges((eds) => [...eds, edgeBetween(currentBranch.parentId, id, currentBranch.choice, currentBranch.choice)]);
      pushBot(
        `Added a **Recommend Product** node for "${currentBranch.choice}". You can pick products from the Pricebook after we save the flow.`
      );
      setTimeout(() => askNextBranch(), 700);
    }
  };

  const handleFollowupQuestion = () => {
    const q = input.trim();
    if (!q) return;
    pushUser(q);
    setInput("");
    const id = newNodeId();
    const xCount = draftNodes.length;
    const newNode = {
      id,
      type: "questionNode",
      position: { x: 80 + (xCount % 4) * 240, y: 320 + (currentBranch.level || 1) * 80 },
      data: { label: q, choices: ["Yes", "No"] },
    };
    setDraftNodes((nds) => [...nds, newNode]);
    setDraftEdges((eds) => [...eds, edgeBetween(currentBranch.parentId, id, currentBranch.choice, currentBranch.choice)]);
    pushBot(
      `Question added. To keep this flow simple I'll auto-add a **Yes \u2192 Diagnosis** and **No \u2192 Diagnosis** stub. You can refine them later.`
    );
    // Auto attach two diagnosis stubs
    setTimeout(() => {
      const yesD = newNodeId();
      const noD = newNodeId();
      const hint1 = DIAGNOSIS_HINTS[Math.floor(Math.random() * DIAGNOSIS_HINTS.length)];
      const hint2 = DIAGNOSIS_HINTS[Math.floor(Math.random() * DIAGNOSIS_HINTS.length)];
      setDraftNodes((nds) => [
        ...nds,
        { id: yesD, type: "diagnosisNode", position: { x: 60 + (xCount % 4) * 240, y: 480 + (currentBranch.level || 1) * 80 }, data: { label: `${q} \u2014 Yes outcome`, recommendation: hint1 } },
        { id: noD, type: "diagnosisNode", position: { x: 200 + (xCount % 4) * 240, y: 480 + (currentBranch.level || 1) * 80 }, data: { label: `${q} \u2014 No outcome`, recommendation: hint2 } },
      ]);
      setDraftEdges((eds) => [
        ...eds,
        edgeBetween(id, yesD, "Yes", "Yes"),
        edgeBetween(id, noD, "No", "No"),
      ]);
      askNextBranch();
    }, 800);
  };

  const handleDiagnosisLabel = () => {
    const label = input.trim();
    if (!label) return;
    pushUser(label);
    setInput("");
    const id = newNodeId();
    const xCount = draftNodes.filter((n) => n.type === "diagnosisNode").length;
    const newNode = {
      id,
      type: "diagnosisNode",
      position: { x: 80 + xCount * 250, y: 460 + (currentBranch.level || 1) * 80 },
      data: { label, recommendation: DIAGNOSIS_HINTS[Math.floor(Math.random() * DIAGNOSIS_HINTS.length)] },
    };
    setDraftNodes((nds) => [...nds, newNode]);
    setDraftEdges((eds) => [...eds, edgeBetween(currentBranch.parentId, id, currentBranch.choice, currentBranch.choice)]);
    pushBot(`Diagnosis "${label}" added for the "${currentBranch.choice}" path.`);
    setTimeout(() => askNextBranch(), 700);
  };

  const askIfDone = () => {
    pushBot(
      `All branches handled. Want to add another question, or are we done? You can refine the recommendations and product picks later in the editor.`,
      { choices: ["Add another question", "I'm done"] }
    );
    setPhase("done");
  };

  const handleDoneChoice = (c) => {
    pushUser(c);
    if (c === "Add another question") {
      pushBot(`Pick the parent question to extend, or just type the next question and I'll attach it to the start node:`);
      setPhase("ask_first_q");
    } else {
      askToSave();
    }
  };

  const askToSave = () => {
    pushBot(
      `Your flow is ready \u2014 **${draftNodes.length} nodes, ${draftEdges.length} connections**. Give it a name to save it to **My Flows**.`
    );
    setFlowName(topic || "Untitled Flow");
    setPhase("naming");
  };

  const handleSave = async () => {
    const name = flowName.trim() || topic || "Untitled Flow";
    try {
      const saved = await saveFlow({ name, nodes: draftNodes, edges: draftEdges });
      pushBot(`Saved as **"${saved.name}"**. You can now select it from My Flows or use it in Diagnostic Guru.`);
      setPhase("complete");
      onSaved?.(saved);
    } catch (e) {
      pushBot("Sorry, saving failed. Please try again.");
    }
  };

  // ---- Render input area based on phase ----
  const renderInput = () => {
    if (phase === "ask_topic") {
      return (
        <TextSubmit value={input} onChange={setInput} onSubmit={handleTopicSubmit} placeholder="e.g., AC not cooling, water heater leaking..." />
      );
    }
    if (phase === "ask_has_list") {
      return <Choices choices={["Generate with AI", "Upload a list", "Build together"]} onPick={handleListChoice} />;
    }
    if (phase === "ai_generating") {
      return (
        <div className="flex items-center gap-2 text-[12.5px] text-slate-600 py-1">
          <Wand2 className="h-4 w-4 text-fuchsia-600 animate-pulse" />
          <span>GPT-4o is drafting your flow\u2026</span>
        </div>
      );
    }
    if (phase === "upload") {
      return (
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="h-9 text-[13px]">
            <Upload className="h-4 w-4 mr-1.5" /> Choose file
          </Button>
          <span className="text-[12px] text-slate-500">.txt or .csv with one question per line</span>
          <input
            type="file"
            ref={fileInputRef}
            accept=".txt,.csv,.pdf"
            hidden
            onChange={(e) => handleQuestionListUpload(e.target.files?.[0])}
          />
        </div>
      );
    }
    if (phase === "ask_first_q") {
      return <TextSubmit value={input} onChange={setInput} onSubmit={handleFirstQuestion} placeholder="e.g., Is the thermostat set to COOL?" />;
    }
    if (phase === "ask_choices") {
      return (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {["Yes, No", "Yes, No, Sometimes", "True, False, Unknown"].map((p) => (
              <button
                key={p}
                onClick={() => handleChoicesSubmit(p)}
                className="text-[11.5px] px-2.5 py-1 rounded-full bg-slate-100 hover:bg-blue-50 hover:text-blue-700 text-slate-600 transition-colors"
              >
                {p}
              </button>
            ))}
          </div>
          <TextSubmit value={input} onChange={setInput} onSubmit={() => handleChoicesSubmit()} placeholder="Yes, No (comma-separated, max 4)" />
        </div>
      );
    }
    if (phase === "ask_branch") {
      return <Choices choices={["Ask another question", "Final Diagnosis", "Recommend Product"]} onPick={handleBranchType} />;
    }
    if (phase === "ask_followup_q") {
      return <TextSubmit value={input} onChange={setInput} onSubmit={handleFollowupQuestion} placeholder={`Next question for "${currentBranch?.choice}" path...`} />;
    }
    if (phase === "ask_diagnosis_label") {
      return <TextSubmit value={input} onChange={setInput} onSubmit={handleDiagnosisLabel} placeholder="e.g., Likely refrigerant leak" />;
    }
    if (phase === "done") {
      return <Choices choices={["Add another question", "I'm done"]} onPick={handleDoneChoice} />;
    }
    if (phase === "naming") {
      return (
        <div className="flex items-center gap-2">
          <input
            value={flowName}
            onChange={(e) => setFlowName(e.target.value)}
            placeholder="Flow name"
            className="flex-1 h-9 px-3 rounded-md border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
          />
          <Button onClick={handleSave} className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white text-[13px]">
            <Save className="h-4 w-4 mr-1.5" /> Save to My Flows
          </Button>
        </div>
      );
    }
    if (phase === "complete") {
      return (
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={reset} className="h-9 text-[13px]">
            <Plus className="h-4 w-4 mr-1.5" /> Build another
          </Button>
          <Button onClick={onClose} className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13px]">
            Done
          </Button>
        </div>
      );
    }
    return null;
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-6">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden border border-slate-200">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-violet-50 via-fuchsia-50 to-blue-50">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-fuchsia-500 to-blue-500 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="text-[14px] font-semibold text-slate-900 flex items-center gap-1.5">
                AI Flow Builder
                <span className="inline-flex items-center text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-gradient-to-br from-fuchsia-500 to-blue-500 text-white">
                  AI
                </span>
              </div>
              <div className="text-[11.5px] text-slate-500">Build a diagnostic flow chart through guided conversation</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-[11.5px] text-slate-500">
              <span className="font-semibold text-slate-700">{draftNodes.length}</span> nodes ·{" "}
              <span className="font-semibold text-slate-700">{draftEdges.length}</span> links
            </div>
            <button onClick={onClose} className="h-8 w-8 rounded-md hover:bg-white/60 flex items-center justify-center transition-colors">
              <X className="h-4 w-4 text-slate-500" />
            </button>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-3 bg-[#FAFBFD]">
          {messages.map((m, i) => (
            <Bubble key={i} m={m} indexedCount={indexedFiles.length} onPick={(c) => {
              if (phase === "ask_has_list") handleListChoice(c);
              else if (phase === "ask_branch") handleBranchType(c);
              else if (phase === "done") handleDoneChoice(c);
              else if (phase === "ask_choices") handleChoicesSubmit(c);
            }} />
          ))}
          {isTyping && (
            <div className="flex gap-2.5 items-start">
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-fuchsia-500 to-blue-500 flex items-center justify-center shrink-0">
                <Bot className="h-4 w-4 text-white" />
              </div>
              <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-3 py-2.5">
                <div className="flex gap-1">
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="h-1.5 w-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-100 bg-white">{renderInput()}</div>
      </div>
    </div>
  );
}

function Bubble({ m, indexedCount, onPick }) {
  if (m.sender === "user") {
    return (
      <div className="flex gap-2.5 items-start justify-end">
        <div className="bg-[#2563EB] text-white rounded-2xl rounded-tr-sm px-3.5 py-2.5 max-w-[78%]">
          <p className="text-[13.5px] leading-relaxed whitespace-pre-wrap">{m.text}</p>
        </div>
        <div className="h-8 w-8 rounded-lg bg-slate-200 flex items-center justify-center shrink-0">
          <User className="h-4 w-4 text-slate-600" />
        </div>
      </div>
    );
  }
  return (
    <div className="flex gap-2.5 items-start">
      <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-fuchsia-500 to-blue-500 flex items-center justify-center shrink-0">
        <Bot className="h-4 w-4 text-white" />
      </div>
      <div className="max-w-[80%]">
        <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-3.5 py-2.5">
          <p className="text-[13.5px] text-slate-800 leading-relaxed whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: sanitizeHtml(fmt(m.text)) }} />
          {m.hint && <p className="mt-1 text-[11.5px] text-slate-500 italic">{m.hint}</p>}
          {m.choices && (
            <div className="mt-2 text-[11px] text-slate-400 italic">↓ Pick an option below</div>
          )}
        </div>
        {indexedCount > 0 && m.text.toLowerCase().includes("referenc") && (
          <div className="mt-1 ml-1 text-[10.5px] text-slate-400 flex items-center gap-1">
            <FileText className="h-2.5 w-2.5" />
            Using {indexedCount} indexed sources
          </div>
        )}
      </div>
    </div>
  );
}

function fmt(t) {
  if (!t) return "";
  return t
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/_(.+?)_/g, "<em>$1</em>");
}

function TextSubmit({ value, onChange, onSubmit, placeholder }) {
  return (
    <div className="flex items-end gap-2">
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            onSubmit();
          }
        }}
        rows={1}
        placeholder={placeholder}
        className="flex-1 resize-none px-3 py-2.5 rounded-lg border border-slate-200 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 max-h-32"
      />
      <Button onClick={onSubmit} disabled={!value.trim()} className="h-10 bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-4">
        <Send className="h-4 w-4" />
      </Button>
    </div>
  );
}

function Choices({ choices, onPick }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {choices.map((c) => (
        <button
          key={c}
          onClick={() => onPick(c)}
          className="px-3 py-1.5 rounded-lg border border-slate-200 hover:border-blue-400 hover:bg-blue-50 text-[13px] font-medium text-slate-700 transition-colors flex items-center gap-1"
        >
          {c}
          <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
        </button>
      ))}
    </div>
  );
}
